import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatCurrency, formatDate } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Trash2, ShoppingCart, X } from 'lucide-react';
import ExportButton from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchasesPage() {
  const { t, language } = useLanguage();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    items: [],
    payment_status: 'pending',
    paid_amount: 0,
    payment_method: 'cash',
    notes: '',
  });
  const [newItem, setNewItem] = useState({ material_id: '', quantity: '', unit_price: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, suppliersRes, materialsRes] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/materials`),
      ]);
      setPurchases(purchasesRes.data);
      setSuppliers(suppliersRes.data);
      setMaterials(materialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    const material = materials.find((m) => m.id === newItem.material_id);
    if (material && newItem.quantity && newItem.unit_price) {
      setFormData({
        ...formData,
        items: [
          ...formData.items,
          {
            material_id: material.id,
            material_name: language === 'ar' && material.name_ar ? material.name_ar : material.name,
            quantity: parseFloat(newItem.quantity),
            unit_price: parseFloat(newItem.unit_price),
          },
        ],
      });
      setNewItem({ material_id: '', quantity: '', unit_price: '' });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find((s) => s.id === supplierId);
    setFormData({
      ...formData,
      supplier_id: supplierId,
      supplier_name: supplier?.name || '',
    });
  };

  const handleMaterialChange = (materialId) => {
    const material = materials.find((m) => m.id === materialId);
    setNewItem({
      ...newItem,
      material_id: materialId,
      unit_price: material?.default_price?.toString() || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) return;
    try {
      await axios.post(`${API}/purchases`, {
        ...formData,
        paid_amount: parseFloat(formData.paid_amount || 0),
      });
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving purchase:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm'))) {
      try {
        await axios.delete(`${API}/purchases/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting purchase:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      supplier_id: '',
      supplier_name: '',
      items: [],
      payment_status: 'pending',
      paid_amount: 0,
      payment_method: 'cash',
      notes: '',
    });
    setNewItem({ material_id: '', quantity: '', unit_price: '' });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  };

  const filteredPurchases = purchases.filter((p) =>
    p.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {t(status)}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="purchases-title">{t('purchases')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة المشتريات من الموردين' : 'Manage purchases from suppliers'}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            endpoint="purchases"
            filename="purchases"
            label={language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="add-purchase-btn">
            <Plus size={18} className="me-2" />
            {t('addPurchase')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10"
              data-testid="purchases-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Purchases Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('date')}</TableHead>
                    <TableHead>{t('supplier')}</TableHead>
                    <TableHead>{language === 'ar' ? 'عدد المنتجات' : 'Items'}</TableHead>
                    <TableHead>{t('total')}</TableHead>
                    <TableHead>{t('paymentStatus')}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                      <TableCell>{formatDate(purchase.date)}</TableCell>
                      <TableCell className="font-medium">{purchase.supplier_name}</TableCell>
                      <TableCell>{purchase.items.length}</TableCell>
                      <TableCell className="font-bold text-primary">
                        {formatCurrency(purchase.total)}
                      </TableCell>
                      <TableCell>{getStatusBadge(purchase.payment_status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(purchase.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-purchase-${purchase.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Purchase Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t('addPurchase')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('supplier')}</Label>
                <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                  <SelectTrigger data-testid="purchase-supplier-select">
                    <SelectValue placeholder={language === 'ar' ? 'اختر المورد' : 'Select supplier'} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('paymentStatus')}</Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                >
                  <SelectTrigger data-testid="purchase-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="partial">{t('partial')}</SelectItem>
                    <SelectItem value="paid">{t('paid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Item */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <Label>{language === 'ar' ? 'إضافة مادة خام' : 'Add Material'}</Label>
              <div className="grid grid-cols-4 gap-2">
                <Select value={newItem.material_id} onValueChange={handleMaterialChange}>
                  <SelectTrigger data-testid="purchase-material-select">
                    <SelectValue placeholder={language === 'ar' ? 'اختر المادة' : 'Select material'} />
                  </SelectTrigger>
                  <SelectContent>
                    {materials.map((material) => (
                      <SelectItem key={material.id} value={material.id}>
                        {language === 'ar' && material.name_ar ? material.name_ar : material.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder={t('quantity')}
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                  data-testid="purchase-quantity-input"
                />
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t('unitPrice')}
                  value={newItem.unit_price}
                  onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })}
                  data-testid="purchase-price-input"
                />
                <Button type="button" onClick={handleAddItem} data-testid="add-item-btn">
                  {t('add')}
                </Button>
              </div>
            </div>

            {/* Items List */}
            {formData.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'ar' ? 'المادة' : 'Material'}</TableHead>
                      <TableHead>{t('quantity')}</TableHead>
                      <TableHead>{t('unitPrice')}</TableHead>
                      <TableHead>{t('total')}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={`${item.material_id}-${index}`}>
                        <TableCell>{item.material_name}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-4 bg-muted/50 flex justify-between items-center">
                  <span className="font-medium">{t('total')}:</span>
                  <span className="text-xl font-bold text-primary">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            )}

            {formData.payment_status !== 'paid' && (
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'المبلغ المدفوع' : 'Paid Amount'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.paid_amount}
                  onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                  data-testid="purchase-paid-input"
                />
              </div>
            )}

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
              >
                <SelectTrigger data-testid="purchase-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{language === 'ar' ? '💵 كاش' : '💵 Cash'}</SelectItem>
                  <SelectItem value="bank">{language === 'ar' ? '🏦 بنك' : '🏦 Bank'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="purchase-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={formData.items.length === 0} data-testid="purchase-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
